/**
 * Domain Search API Client
 *
 * Interfaces with the niftee-domain-search worker (domains.niftee.ai)
 * for fast domain availability checking via Fastly Domain Research API.
 */

// Domain search worker URL - update for production
const DOMAIN_SEARCH_URL = import.meta.env.VITE_DOMAIN_SEARCH_URL || 'https://domains.niftee.ai';

// Main orchestrator API for pricing and purchase
const API_URL =
  import.meta.env.VITE_API_URL ||
  'https://niftee-orchestrator.jason-a6e.workers.dev';

export interface TldResult {
  tld: string;
  domain: string;
  available: boolean;
  status: string;
}

export interface DomainSearchResponse {
  query: string;
  results: TldResult[];
  extended_available: boolean;
  timestamp: string;
}

export interface DomainConfirmResponse {
  domain: string;
  available: boolean;
  status: string;
  precise: boolean;
  timestamp: string;
}

export interface DomainPriceResponse {
  domain: string;
  available: boolean;
  price_cents: number;
  renewal_price_cents: number;
  currency: string;
  tld: string;
}

export interface DomainPurchaseResponse {
  success: boolean;
  message: string;
  purchaseId: string;
  domain: string;
  status: 'processing' | 'propagating' | 'active' | 'test-mode';
}

/**
 * Search for domain availability across multiple TLDs.
 * Uses Fastly Domain Research API via our edge worker for instant results.
 *
 * @param name - The domain name to search (without TLD)
 * @param options - Search options
 * @returns Search results with availability for each TLD
 */
export async function searchDomains(
  name: string,
  options?: {
    extended?: boolean;
    sessionId?: string;
    customerId?: string;
    source?: 'onboarding' | 'landing' | 'portal';
  }
): Promise<DomainSearchResponse> {
  const params = new URLSearchParams({ name });

  if (options?.extended) {
    params.set('extended', '1');
  }
  if (options?.sessionId) {
    params.set('session_id', options.sessionId);
  }
  if (options?.customerId) {
    params.set('customer_id', options.customerId);
  }
  if (options?.source) {
    params.set('source', options.source);
  }

  const response = await fetch(`${DOMAIN_SEARCH_URL}/check?${params}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(error.error || 'Failed to search domains');
  }

  return response.json();
}

/**
 * Get precise availability for a single domain.
 * Uses Fastly's precise status check (more expensive but definitive).
 *
 * @param domain - Full domain including TLD (e.g., "mybrand.com")
 * @returns Precise availability status
 */
export async function confirmDomainAvailability(
  domain: string
): Promise<DomainConfirmResponse> {
  const response = await fetch(
    `${DOMAIN_SEARCH_URL}/confirm?domain=${encodeURIComponent(domain)}`
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(error.error || 'Failed to confirm domain availability');
  }

  return response.json();
}

/**
 * Get the actual price for a domain from Porkbun (with niftee markup).
 * Called when user selects a domain to see the real price before purchase.
 *
 * @param domain - Full domain including TLD
 * @returns Pricing information
 */
export async function getDomainPrice(
  domain: string
): Promise<DomainPriceResponse> {
  const response = await fetch(
    `${API_URL}/api/domains/price?domain=${encodeURIComponent(domain)}`
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(error.error || 'Failed to get domain price');
  }

  return response.json();
}

/**
 * Purchase a domain during onboarding.
 * Creates a Stripe subscription and registers the domain via Porkbun.
 * Charges the customer's card on file directly (no redirect).
 *
 * @param sessionId - Onboarding session ID
 * @param domain - Full domain to purchase
 * @returns Purchase confirmation with status
 */
export async function purchaseDomain(
  sessionId: string,
  domain: string
): Promise<DomainPurchaseResponse> {
  const response = await fetch(
    `${API_URL}/api/onboarding/${sessionId}/purchase-domain`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ domain }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(error.error || 'Failed to purchase domain');
  }

  return response.json();
}

/**
 * Get the list of available TLDs.
 *
 * @returns Default and extended TLD lists
 */
export async function getAvailableTlds(): Promise<{
  default: string[];
  extended: string[];
  all: string[];
}> {
  const response = await fetch(`${DOMAIN_SEARCH_URL}/tlds`);

  if (!response.ok) {
    throw new Error('Failed to get TLD list');
  }

  return response.json();
}
